import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step", "nextButton", "prevButton", "submitButton", "firstStepSubmitButton", "progressStep", "progressLine", "progressCircle", "progressText"]
  static values = {
    currentStep: { type: Number, default: 0 },
    completedSteps: { type: Array, default: [] },
    editMode: { type: Boolean, default: false },
    scrollOffset: { type: Number, default: 80 },
    scrollToTop: { type: Boolean, default: false }
  }

  initialize() {
    this.hasRenderedInitialStep = false
  }

  static get styles() {
    return {
      completed: {
        circle: ['bg-green-500', 'text-white', 'border-green-500', 'border'],
        text: ['text-green-600', 'font-medium']
      },
      current: {
        circle: ['bg-trust', 'text-black', 'border-trust-deep', 'scale-110', 'shadow-md', 'border'],
        text: ['text-trust-deep', 'font-semibold']
      },
      upcoming: {
        circle: ['bg-white', 'text-gray-500', 'border-gray-300', 'border'],
        text: ['text-gray-500', 'font-medium']
      }
    }
  }

  get allStyles() {
    if (!this._allStyles) {
      const styles = this.constructor.styles
      this._allStyles = {
        circle: [...new Set(Object.values(styles).flatMap(s => s.circle))],
        text: [...new Set(Object.values(styles).flatMap(s => s.text))]
      }
    }
    return this._allStyles
  }

  connect() {
    // In edit mode, mark all steps as completed from the start
    if (this.editModeValue) {
      this.completedStepsValue = Array.from({ length: this.stepTargets.length }, (_, i) => i)
      // Update progress to make all steps clickable immediately
      this.updateProgress()
    }
    // Initial setup handled by currentStepValueChanged
  }

  next(event) {
    event.preventDefault()
    if (this.validateCurrentStep()) {
      // Mark current step as completed
      if (!this.completedStepsValue.includes(this.currentStepValue)) {
        this.completedStepsValue = [...this.completedStepsValue, this.currentStepValue]
      }
      this.currentStepValue++
    }
  }

  previous(event) {
    event.preventDefault()
    this.currentStepValue--
  }

  goToStep(event) {
    event.preventDefault()
    const targetStep = Number(event.currentTarget.dataset.step)

    // Don't do anything if clicking the current step
    if (targetStep === this.currentStepValue) {
      return
    }

    // Allow navigation to completed steps or the next incomplete step
    const isCompleted = this.completedStepsValue.includes(targetStep)
    const maxCompletedStep = this.completedStepsValue.length > 0
      ? Math.max(...this.completedStepsValue)
      : -1
    const isNextStep = targetStep === maxCompletedStep + 1

    if (isCompleted || isNextStep) {
      if (targetStep > this.currentStepValue) {
        // Validate all steps between current and target when jumping forward
        if (this.validateStepsUpTo(targetStep)) {
          // Mark all intermediate steps as completed
          for (let i = this.currentStepValue; i < targetStep; i++) {
            if (!this.completedStepsValue.includes(i)) {
              this.completedStepsValue = [...this.completedStepsValue, i]
            }
          }
          this.currentStepValue = targetStep
        }
      } else {
        // Allow backward navigation without re-validation
        this.currentStepValue = targetStep
      }
    }
  }

  validateStepsUpTo(targetStep) {
    for (let i = this.currentStepValue; i < targetStep; i++) {
      if (!this.validateStep(i)) {
        return false
      }
    }
    return true
  }

  validateStep(stepIndex) {
    const stepElement = this.stepTargets[stepIndex]
    const fields = Array.from(stepElement.querySelectorAll("input, select, textarea"))
      .filter(field => !this.isTomSelectControlInput(field))

    for (const field of fields) {
      if (!this.validateField(field)) {
        this.scrollToInvalidField(field)
        return false
      }
    }

    return true
  }

  validateField(field) {
    if (field.disabled) return true

    if (field.tagName === "SELECT" && field.tomselect && field.required) {
      if (!field.value) {
        field.dispatchEvent(new Event("invalid", { bubbles: false, cancelable: true }))
        return false
      }
      return true
    }

    return field.reportValidity()
  }

  scrollToInvalidField(field) {
    const target = this.focusTarget(field)
    if (!target) return

    target.scrollIntoView({ behavior: "smooth", block: "center" })

    const focusable = target.querySelector?.("input") || target
    window.setTimeout(() => focusable.focus?.({ preventScroll: true }), 150)
  }

  focusTarget(field) {
    if (field.closest(".ts-wrapper")) {
      return field.closest(".ts-wrapper")
    }

    if (field.tomselect || field.classList.contains("tomselected")) {
      const control = field.parentElement?.querySelector(".ts-control")
      if (control) return control
    }

    if (field.type === "radio" || field.type === "checkbox") {
      return field.closest(".form-field") || field
    }

    return field
  }

  isTomSelectControlInput(field) {
    return field.tagName === "INPUT" && field.closest(".ts-wrapper")
  }

  currentStepValueChanged(currentValue, previousValue) {
    if (!this.hasRenderedInitialStep || previousValue === undefined) {
      this.showStep(currentValue)
      this.hasRenderedInitialStep = true
    } else {
      this.animateStepChange(currentValue, previousValue)
    }
  }

  showStep(index) {
    this.stepTargets.forEach((element, i) => {
      element.classList.toggle("hidden", i !== index)
    })
    this.updateProgress()
    this.updateButtons()
  }

  async animateStepChange(currentIndex, previousIndex) {
    const direction = currentIndex > previousIndex ? 1 : -1
    const previousStep = this.stepTargets[previousIndex]
    const currentStep = this.stepTargets[currentIndex]

    const isRtl = document.documentElement.dir === 'rtl'
    const outTransform = direction === 1
      ? `translateX(${isRtl ? '20px' : '-20px'})`
      : `translateX(${isRtl ? '-20px' : '20px'})`
    const inTransformStart = direction === 1
      ? `translateX(${isRtl ? '-20px' : '20px'})`
      : `translateX(${isRtl ? '20px' : '-20px'})`

    const fadeOut = previousStep.animate([
      { opacity: 1, transform: 'translateX(0)' },
      { opacity: 0, transform: outTransform }
    ], {
      duration: 250,
      easing: 'ease-in',
      fill: 'forwards'
    })

    await fadeOut.finished
    previousStep.classList.add('hidden')
    fadeOut.cancel()

    currentStep.classList.remove('hidden')
    this.scrollToForm()

    const fadeIn = currentStep.animate([
      { opacity: 0, transform: inTransformStart },
      { opacity: 1, transform: 'translateX(0)' }
    ], {
      duration: 250,
      easing: 'ease-out',
      fill: 'forwards'
    })

    this.updateProgress()
    this.updateButtons()

    await fadeIn.finished
  }

  scrollToForm() {
    if (this.scrollToTopValue) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    const absoluteTop = window.scrollY + this.element.getBoundingClientRect().top
    const top = Math.max(0, absoluteTop - this.scrollOffsetValue)

    window.scrollTo({
      top,
      behavior: 'smooth'
    })
  }

  updateProgress() {
    if (!this.hasProgressStepTarget) return

    const { circle: circleClasses, text: textClasses } = this.allStyles

    this.progressStepTargets.forEach((stepElement, index) => {
      const circle = this.progressCircleTargets[index]
      const text = this.progressTextTargets[index]

      circle.classList.remove(...circleClasses)
      text.classList.remove(...textClasses)

      stepElement.classList.remove('cursor-pointer', 'pointer-events-none')

      // Determine if this step is clickable
      const isCompleted = this.completedStepsValue.includes(index)
      const maxCompletedStep = this.completedStepsValue.length > 0
        ? Math.max(...this.completedStepsValue)
        : -1
      const isNextStep = index === maxCompletedStep + 1
      const isClickable = isCompleted || isNextStep

      if (isClickable) {
        stepElement.classList.add('cursor-pointer')
      } else {
        stepElement.classList.add('pointer-events-none')
      }

      // Determine visual state
      // Priority: current step > completed > upcoming
      let state = 'upcoming'
      if (index === this.currentStepValue) {
        state = 'current'
      } else if (isCompleted) {
        state = 'completed'
      }

      const style = this.constructor.styles[state]
      circle.classList.add(...style.circle)
      text.classList.add(...style.text)

      if (state === 'completed') {
        circle.innerHTML = `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>`
      } else {
        circle.textContent = index + 1
      }
    })

    if (this.hasProgressLineTarget) {
      this.progressLineTargets.forEach((element, index) => {
        element.classList.remove('bg-trust', 'bg-gray-200', 'bg-green-500')

        const isCompleted = this.completedStepsValue.includes(index)
        if (isCompleted && index < this.currentStepValue) {
          element.classList.add('bg-green-500')
        } else {
          element.classList.add('bg-gray-200')
        }
      })
    }
  }

  updateButtons() {
    const isFirstStep = this.currentStepValue === 0
    const isLastStep = this.currentStepValue === this.stepTargets.length - 1

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle("hidden", isFirstStep)
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("hidden", isLastStep)
    }

    if (this.hasSubmitButtonTarget) {
      const showSubmit = this.editModeValue || isLastStep
      this.submitButtonTarget.classList.toggle("hidden", !showSubmit)
    }

    if (this.hasFirstStepSubmitButtonTarget) {
      this.firstStepSubmitButtonTarget.classList.toggle("hidden", !isFirstStep)
    }
  }

  validateCurrentStep() {
    return this.validateStep(this.currentStepValue)
  }
}
